import * as _ from 'lodash';
import * as hash from 'object-hash';
import * as moment from 'moment';
import { Component, FormEvent } from 'react';
import { connect, Dispatch } from 'react-redux';
import { AppActionCreators, CenterActionCreators, IApplicationState, IAppState, ICenterProfileResponse } from '@nkr/store/index';
import { IDocumentsVersion, IDropDownItem, IListItem, IProfileData, IUpdateCenterProfileRequest } from '@nkr/models/index';
import { Col, ControlLabel, FormControl, FormGroup, Label, Radio, Row } from 'react-bootstrap';
import { IProfileDataValidation } from './validation';
import { IErrorItem } from '../../error-item';
import { initialProfileData, initialProfileValidationData } from '../initial-data';
import { profileNavKey } from '../../profile-nav-key';
import { FormWrapper } from '../styles';
import { Validate } from '../../validate';
import { toast } from 'react-toastify';
import { VersionedFileEntityType } from '@nkr/enums/versioned-file-entity-type';
import { downloadFile } from '@nkr/services/download-file';
import { OrganType } from '@nkr/enums/organ-type';
import UploadFile, { NewFileDisplayType, UploadFileType } from '../../upload-file';
import CenterProfileNav from '../edit-nav';
import MaskedInput from 'react-maskedinput';

interface IProps {
    centerId: number,
    isAdmin: boolean,
    isAdminUser: boolean,
    hasDataChanges: boolean,
    documentVersionList: IDocumentsVersion[],
    timeZoneList: IDropDownItem[],
    coordinatorList: IListItem[],
    onTabClick: (href: string) => void,
    onDataChanged: (hasDataChanged: boolean) => void,
    updateErrors: (list: IErrorItem[]) => void
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getData: (centerId: number, organType?: number) => Promise<ICenterProfileResponse>,
    updateData: (data: IUpdateCenterProfileRequest) => Promise<ICenterProfileResponse>,
    getW9File: (centerId: number) => Promise<any>,
    getCmsFile: (centerId: number) => Promise<any>,
    getVersionedFile: (centerId: number, id: number) => Promise<any>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    data: IProfileData,
    validation: IProfileDataValidation,
    hash: string,
    isDirty: boolean,
    isValid: boolean
}

class ProfileDataTab extends Component<TProps, IState> {
    readonly state: IState = {
        data: initialProfileData,
        validation: initialProfileValidationData,
        hash: '',
        isDirty: false,
        isValid: true
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting center\'s profile, one moment please...').then(() => {
            this.props.getData(this.props.centerId, this.props.app.site.organType).then(response => {
                let data: IProfileData = { ...response.data.results, newAchAccountNumber: '' };

                this.setState({
                    data,
                    hash: hash(data)
                });

                this.props.hideLoading();
            });
        });
    }

    save = () => {
        let {
            webUrl,
            name,
            earliestOrganPickupTime,
            latestOrganPickupTime,
            timezone,
            avgCostLDEvalWorkup,
            achRoutingNumber,
            newAchAccountNumber: achAccountNumber,
            invoiceDaysToRemit,
            streamlinedHospitalBilling,
            physicianDonorNephrectomyCharges,
            provideSwapChecklists,
            canUtilizeA2toBPatients,
            newW9File,
            newCmsFile,
            salesTaxFile,
            isTaxExempt,
            taxExemptExpirationDate,
            leadCoordinatorId
        } = this.state.data;

        let w9 = Validate.getFileInfo(newW9File).file;
        let cmsLetter = Validate.getFileInfo(newCmsFile).file;

        let data: IUpdateCenterProfileRequest = {
            id: this.props.centerId,
            webUrl,
            name,
            earliestOrganPickupTime,
            latestOrganPickupTime,
            timezone,
            avgCostLDEvalWorkup: avgCostLDEvalWorkup != '' ? parseFloat(avgCostLDEvalWorkup) : null,
            achRoutingNumber,
            achAccountNumber,
            invoiceDaysToRemit: invoiceDaysToRemit != '' ? parseInt(invoiceDaysToRemit) : null,
            streamlinedHospitalBilling,
            physicianDonorNephrectomyCharges: physicianDonorNephrectomyCharges != '' ? parseFloat(physicianDonorNephrectomyCharges) : null,
            provideSwapChecklists,
            isTaxExempt,
            taxExemptExpirationDate,
            canUtilizeA2toBPatients,
            w9,
            cmsLetter,
            salesTaxExemptCert: {
                fileType: salesTaxFile.typeId,
                fileVersion: salesTaxFile.newFile != null ? salesTaxFile.newFile.documentsVersionId : null,
                fileDate: salesTaxFile.newFile != null ? salesTaxFile.newFile.date : null,
                file: salesTaxFile.newFile != null ? salesTaxFile.newFile.file : null
            },
            leadCoordinatorId,
            organType: this.props.app.site.organType
        };

        this.props.showLoading('Saving Center Profile data, one moment please...').then(() => {
            this.props.updateData(data).then(response => {
                this.props.hideLoading();

                if (response.data.success) {
                    let data: IProfileData = { ...response.data.results, newAchAccountNumber: '' };

                    this.setState({
                        data,
                        hash: hash(data),
                    }, () => {
                        toast.success('Center profile updated successfully');

                        this.props.onDataChanged(false);
                    });
                }
            });
        });
    }

    validateBeforeSave = () => {
        let validation = Validate.validateProfileData(this.state.data);
        let isValid = Validate.isProfileDataValid(validation);
        let errorList = Validate.getInvalidProfileDataItems(validation).filter(x => x.show);

        this.setState({
            validation,
            isValid,
            isDirty: true,
        }, () => {
            this.props.updateErrors(errorList);

            if (this.state.isValid) {
                this.save();
            } else {
                window.scrollTo(0, 0);
            }
        });
    }

    handleFieldChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(this.state.data);

        if (name == 'name') {
            data.name = value;
        }

        if (name == 'webUrl') {
            data.webUrl = value;
        }

        if (name == 'earliestOrganPickupTime') {
            data.earliestOrganPickupTime = value;
        }

        if (name == 'latestOrganPickupTime') {
            data.latestOrganPickupTime = value;
        }

        if (name == 'timezone') {
            data.timezone = value;
        }

        if (name == 'avgCostLDEvalWorkup') {
            data.avgCostLDEvalWorkup = value;
        }

        if (name == 'achRoutingNumber') {
            data.achRoutingNumber = value;
        }

        if (name == 'newAchAccountNumber') {
            data.newAchAccountNumber = value;
        }

        if (name == 'physicianDonorNephrectomyCharges') {
            data.physicianDonorNephrectomyCharges = value;
        }

        if (name == 'streamlinedHospitalBilling') {
            data.streamlinedHospitalBilling = parseInt(value) == 1;
        }

        if (name == 'provideSwapChecklists') {
            data.provideSwapChecklists = parseInt(value) == 1;
        }

        if (name == 'canUtilizeA2toBPatients') {
            data.canUtilizeA2toBPatients = parseInt(value) == 1;
        }

        if (name == 'taxExemptExpirationDate') {
            data.taxExemptExpirationDate = value;
        }

        if (name == 'isTaxExempt') {
            data.isTaxExempt = parseInt(value) == 1;
        }

        if (name == 'leadCoordinatorId') {
            data.leadCoordinatorId = parseInt(value);
        }

        let newHash = hash(data);

        this.setState({
            data
        }, () => {
            this.props.onDataChanged(this.state.hash != newHash);
        });
    }

    handleFileChange = (fileEntityType: VersionedFileEntityType, file: File, documentsVersionId: number, date: string) => {
        let data = _.cloneDeep(this.state.data);

        if (fileEntityType == VersionedFileEntityType.W9) {
            data.newW9File = { file, date: '', documentsVersionId: 0 };
        }

        if (fileEntityType == VersionedFileEntityType.Cms) {
            data.newCmsFile = { file, date: '', documentsVersionId: 0 };
        }

        if (fileEntityType == VersionedFileEntityType.CenterTaxExemptionCertificate) {
            data.salesTaxFile.newFile = { file, date, documentsVersionId };
        }

        let newHash = hash(data);

        this.setState({
            data
        }, () => {
            this.props.onDataChanged(newHash != this.state.hash)
        });
    }

    handleFileRemove = (fileEntityType: VersionedFileEntityType) => {
        let data = _.cloneDeep(this.state.data);

        if (fileEntityType == VersionedFileEntityType.W9) {
            data.newW9File = null;
        }

        if (fileEntityType == VersionedFileEntityType.Cms) {
            data.newCmsFile = null;
        }

        if (fileEntityType == VersionedFileEntityType.CenterTaxExemptionCertificate) {
            data.salesTaxFile.newFile = null;
        }

        this.setState({
            data
        }, () => {
            let dataChanged = this.state.data.newW9File != null || this.state.data.newCmsFile != null || (this.state.data.isTaxExempt && this.state.data.salesTaxFile.newFile == null);
            this.props.onDataChanged(dataChanged);
        });
    }

    handleDownloadW9File = () => {
        this.props.getW9File(this.props.centerId).then(response => {
            downloadFile(response.data, `center${this.props.centerId}_w9.pdf`, response.headers.contentType);
        });
    }

    handleDownloadCmsFile = () => {
        this.props.getCmsFile(this.props.centerId).then(response => {
            downloadFile(response.data, `center${this.props.centerId}_cms.pdf`, response.headers.contentType);
        });
    }

    handleFileDownload = (id: number) => {
        this.props.getVersionedFile(this.props.centerId, id).then(response => {
            downloadFile(response.data, `centerSalesTaxExemptFile.pdf`, response.headers.contentType);
        });
    }

    render = () => {
        const { validation } = this.state;
        const today = moment().format('MM/DD/YYYY');

        const {
            name, webUrl,
            earliestOrganPickupTime, latestOrganPickupTime,
            timezone,
            w9FormPath, cmsFormPath,
            newW9File, newCmsFile, salesTaxFile,
            achRoutingNumber,
            achAccountNumber,
            physicianDonorNephrectomyCharges,
            provideSwapChecklists,
            isTaxExempt,
            taxExemptExpirationDate, leadCoordinatorId
        } = this.state.data;

        const backAccountNumberText = "********" + achAccountNumber.substring(achAccountNumber.length - 4);
        const dvList = this.props.documentVersionList.filter(x => x.entityType == (salesTaxFile != null ? salesTaxFile.typeId : 0));

        const isNkr = this.props.app.site.organType == OrganType.Kidney;

        return (
            <>
                <CenterProfileNav
                    activeKey={profileNavKey.CENTER_DATA}
                    isAdmin={this.props.isAdmin}
                    isAdminUser={this.props.isAdminUser}
                    centerId={this.props.centerId}
                    hasChanges={this.props.hasDataChanges}
                    onTabClick={this.props.onTabClick}
                    onSave={this.validateBeforeSave}
                />

                <div style={{ padding: 10, borderWidth: '1px 1px 1px 1px', borderStyle: 'solid', borderColor: '#ddd', borderRadius: 4 }}>
                    <FormWrapper horizontal>
                        <FormGroup validationState={!validation.name ? 'error' : null}>
                            <Col xs={12} lg={3} lgOffset={1} componentClass={ControlLabel}>
                                Center Name:
                            </Col>
                            <Col xs={12} lg={3}>
                                <FormControl
                                    name='name'
                                    value={name}
                                    onChange={this.handleFieldChange}
                                />
                            </Col>
                        </FormGroup>

                        <FormGroup validationState={!validation.webUrl ? 'error' : null}>
                            <Col xs={12} lg={3} lgOffset={1} componentClass={ControlLabel}>
                                Web URL:
                            </Col>
                            <Col xs={12} lg={3}>
                                <FormControl
                                    name='webUrl'
                                    value={webUrl}
                                    onChange={this.handleFieldChange}
                                />
                            </Col>
                        </FormGroup>

                        {isNkr && (
                            <>
                                <FormGroup validationState={validation.earliestPickupTime != 1 ? 'error' : null}>
                                    <Col xs={12} lg={3} lgOffset={1} componentClass={ControlLabel}>
                                        Earliest Organ Pickup Time:
                                    </Col>
                                    <Col xs={12} lg={2}>
                                        <MaskedInput
                                            name='earliestOrganPickupTime'
                                            mask='11:11'
                                            placeholder='hh:mm'
                                            className='form-control'
                                            value={earliestOrganPickupTime}
                                            onChange={this.handleFieldChange} />
                                    </Col>
                                </FormGroup>

                                <FormGroup validationState={!validation.latestOrganPickupTime ? 'error' : null}>
                                    <Col xs={12} lg={3} lgOffset={1} componentClass={ControlLabel}>
                                        Latest Organ Dropoff Time:
                                    </Col>
                                    <Col xs={12} lg={2}>
                                        <MaskedInput
                                            name='latestOrganPickupTime'
                                            mask='11:11'
                                            placeholder='hh:mm'
                                            className='form-control'
                                            value={latestOrganPickupTime}
                                            onChange={this.handleFieldChange} />
                                    </Col>
                                </FormGroup>
                            </>
                        )}

                        <FormGroup validationState={!validation.timezone ? 'error' : null}>
                            <Col xs={12} lg={3} lgOffset={1} componentClass={ControlLabel}>
                                Time Zone:
                            </Col>
                            <Col xs={12} lg={2}>
                                <FormControl
                                    componentClass='select'
                                    name='timezone'
                                    value={timezone}
                                    onChange={this.handleFieldChange}
                                >
                                    <option value={-1}>- select -</option>
                                    {this.props.timeZoneList.map(tz =>
                                        <option value={tz.value}>{tz.display}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <UploadFile
                            fileEntityType={VersionedFileEntityType.W9}
                            labelText='W9'
                            modalTitle='W9'
                            valid={true}
                            disable={false}
                            type={UploadFileType.Standard}
                            newFile={newW9File}
                            newFileDisplayType={NewFileDisplayType.CenterData}
                            marginBottom={7}
                            dimensions={{ label: 3, labelOffset: 1, existing: 1, select: 2, new: { size: 5, offset: 5 } }}
                            existingFile={w9FormPath}
                            downloadById={true}
                            onSelectFile={this.handleFileChange}
                            onRemoveFile={this.handleFileRemove}
                            onDownloadFile={this.handleDownloadW9File}
                        />

                        <UploadFile
                            fileEntityType={VersionedFileEntityType.Cms}
                            labelText='CMS Letter of Good Standing'
                            modalTitle='CMS Letter'
                            valid={true}
                            disable={false}
                            type={UploadFileType.Standard}
                            newFile={newCmsFile}
                            newFileDisplayType={NewFileDisplayType.CenterData}
                            marginBottom={7}
                            dimensions={{ label: 3, labelOffset: 1, existing: 1, select: 2, new: { size: 5, offset: 5 } }}
                            existingFile={cmsFormPath}
                            downloadById={true}
                            onSelectFile={this.handleFileChange}
                            onRemoveFile={this.handleFileRemove}
                            onDownloadFile={this.handleDownloadCmsFile}
                        />

                        <FormGroup validationState={!validation.timezone ? 'error' : null}>
                            <Col xs={12} lg={3} lgOffset={1} componentClass={ControlLabel}>
                                ABA Routing Number:
                            </Col>
                            <Col xs={12} lg={2}>
                                <FormControl
                                    name='achRoutingNumber'
                                    readOnly={true}
                                    value={achRoutingNumber}
                                    onChange={this.handleFieldChange}
                                />
                            </Col>
                        </FormGroup>

                        <FormGroup validationState={!validation.timezone ? 'error' : null}>
                            <Col xs={12} lg={3} lgOffset={1} componentClass={ControlLabel}>
                                Bank Account Number:
                            </Col>
                            <Col xs={12} lg={2}>
                                <FormControl
                                    name='bankAccountNumber'
                                    readOnly={true}
                                    value={backAccountNumberText}
                                    onChange={this.handleFieldChange}
                                />
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={3} lgOffset={1} componentClass={ControlLabel}>
                                Is your Transplant Center Sales Tax Exempt?
                            </Col>
                            <Col xs={12} lg={5}>
                                <Radio id='ctrIsTaxExemptYes' name="isTaxExempt" inline value='1' checked={isTaxExempt == true} onChange={this.handleFieldChange}>Yes</Radio>{' '}
                                <Radio id='ctrIsTaxExemptNo' name="isTaxExempt" inline value='0' checked={isTaxExempt == false} onChange={this.handleFieldChange}>No</Radio>{' '}
                            </Col>
                        </FormGroup>

                        {this.state.data.isTaxExempt && (
                            <>
                                <UploadFile
                                    fileEntityType={VersionedFileEntityType.CenterTaxExemptionCertificate}
                                    labelText='Sales Tax Exemption Certificate'
                                    modalTitle='Tax Exempt Certificate'
                                    valid={validation.newTaxFile}
                                    disable={false}
                                    withVersionHistory={false}
                                    documentsVersions={dvList}
                                    type={UploadFileType.WithVersionControl}
                                    newFileDisplayType={NewFileDisplayType.AdminData}
                                    newFile={salesTaxFile.newFile}
                                    versionedFiles={salesTaxFile.files}
                                    dimensions={{ label: 3, labelOffset: 1, existing: 1, select: 2, new: { size: 5, offset: 5 } }}
                                    validationState={!validation.newTaxFile ? 'error' : null}
                                    onSelectFile={this.handleFileChange}
                                    onRemoveFile={this.handleFileRemove}
                                    onDownloadFile={this.handleFileDownload}
                                />

                                <FormGroup validationState={!validation.taxExemptExpirationDate ? 'error' : null}>
                                    <Col xs={12} lg={3} lgOffset={1} componentClass={ControlLabel}>
                                        Exemption Certificate Expiration Date:&nbsp;
                                    </Col>
                                    <Col xs={12} lg={2}>
                                        <div style={{ marginRight: 5 }}>
                                            <MaskedInput
                                                name='taxExemptExpirationDate'
                                                mask='11-11-1111'
                                                placeholder='mm-dd-yyyy'
                                                className='form-control'
                                                value={taxExemptExpirationDate}
                                                onChange={this.handleFieldChange}
                                            />
                                        </div>

                                        {moment(taxExemptExpirationDate).isBefore(today) && (
                                            <Row style={{ marginBottom: 10 }}>
                                                <Col xs={12} lg={8} >
                                                    <Label bsStyle='danger'>
                                                        <>The expiration date is earlier than today's date. Please set it to a future date.</>
                                                    </Label>
                                                </Col>
                                            </Row>
                                        )}
                                    </Col>
                                </FormGroup>
                            </>
                        )}

                        {isNkr && (
                            <>
                                <FormGroup validationState={!validation.timezone ? 'error' : null}>
                                    <Col xs={12} lg={3} lgOffset={1} componentClass={ControlLabel}>
                                        Physician Donor Nephrectomy - Medicare Participating:
                                    </Col>
                                    <Col xs={12} lg={1}>
                                        <FormControl
                                            name='physicianDonorNephrectomyCharges'
                                            value={physicianDonorNephrectomyCharges}
                                            onChange={this.handleFieldChange}
                                        />
                                    </Col>
                                </FormGroup>

                                <FormGroup style={{ display: 'flex', alignItems: 'center', height: 30 }}>
                                    <Col xs={12} lg={3} lgOffset={1} componentClass={ControlLabel}>
                                        Provide Swap Checklists?
                                    </Col>
                                    <Col xs={12} lg={5}>
                                        <Radio id='provideSwapChecklistsYes' name='provideSwapChecklists' inline value={1} checked={provideSwapChecklists} onChange={this.handleFieldChange}>Yes</Radio>{' '}
                                        <Radio id='provideSwapChecklistsNo' name='provideSwapChecklists' inline value={0} checked={!provideSwapChecklists} onChange={this.handleFieldChange}>No</Radio>{' '}
                                    </Col>
                                </FormGroup>
                            </>
                        )}

                        <FormGroup validationState={this.state.isDirty && !validation.leadCoordinator ? 'error' : null}>
                            <Col xs={12} lg={3} lgOffset={1} componentClass={ControlLabel}>
                                Lead Coordinator:
                            </Col>
                            <Col xs={12} lg={2}>
                                <FormControl
                                    componentClass='select'
                                    name='leadCoordinatorId'
                                    value={leadCoordinatorId}
                                    onChange={this.handleFieldChange}
                                >
                                    <option value={0}>- select -</option>
                                    {this.props.coordinatorList.map(item =>
                                        <option value={item.id}>{item.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>
                    </FormWrapper>
                </div>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getData: (centerId, organType) => {
            return dispatch(CenterActionCreators.getCenterProfile(centerId, organType));
        },
        updateData: (data) => {
            return dispatch(CenterActionCreators.updateCenterProfile(data));
        },
        getW9File: (centerId) => {
            return dispatch(CenterActionCreators.getW9File(centerId));
        },
        getCmsFile: (centerId) => {
            return dispatch(CenterActionCreators.getCmsFile(centerId));
        },
        getVersionedFile: (centerId, id) => {
            return dispatch(CenterActionCreators.getVersionedFile(centerId, id));
        }
    }
}

export default connect<IStateProps, {}, IProps>(
    mapStateToProps,
    mapDispatchToProps)(ProfileDataTab);
